import * as THREE from "three";
import { CAMERA } from "../config/constants.js";
import { angleLerp, deg2rad } from "./util.js";

/**
 * CameraRig is the Minecraft-Dungeons-style camera: a fixed downward tilt
 * that orbits the player in snap steps (Q/E). It also exposes the ground
 * forward/right vectors so player movement stays relative to the current
 * view rotation.
 */
export class CameraRig {
  constructor(aspect) {
    this.camera = new THREE.PerspectiveCamera(CAMERA.fov, aspect, 0.1, 200);
    this.target = new THREE.Vector3(0, 0, 0);   // point we look at (player)
    this.smoothTarget = new THREE.Vector3(0, 0, 0);

    this.yaw = deg2rad(45);        // current orbit angle
    this.targetYaw = this.yaw;     // eased toward on rotate
    this.pitch = deg2rad(CAMERA.pitch);
    this.distance = CAMERA.distance;

    this._forward = new THREE.Vector3();
    this._right = new THREE.Vector3();
  }

  setAspect(aspect) {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  rotateLeft() { this.targetYaw -= deg2rad(CAMERA.snapStep); }
  rotateRight() { this.targetYaw += deg2rad(CAMERA.snapStep); }
  rotateBy(delta) { this.targetYaw += delta; }

  // Snap the camera instantly to a target (used on floor load).
  snapTo(x, z, y = 0) {
    this.target.set(x, y, z);
    this.smoothTarget.set(x, y, z);
    this.yaw = this.targetYaw;
    this._place();
  }

  update(dt, targetPos) {
    this.target.set(targetPos.x, targetPos.y || 0, targetPos.z);
    // ease follow + rotation (height eases too, so the view rides stairs)
    this.smoothTarget.lerp(this.target, CAMERA.followLerp);
    this.yaw = angleLerp(this.yaw, this.targetYaw, CAMERA.rotateLerp);
    this._place();
  }

  _place() {
    const horiz = Math.cos(this.pitch) * this.distance;
    const y = Math.sin(this.pitch) * this.distance + this.smoothTarget.y;
    const cx = this.smoothTarget.x + Math.sin(this.yaw) * horiz;
    const cz = this.smoothTarget.z + Math.cos(this.yaw) * horiz;
    this.camera.position.set(cx, y, cz);
    this.camera.lookAt(this.smoothTarget.x, this.smoothTarget.y, this.smoothTarget.z);
    this._updateBasis();
  }

  // Ground-projected forward/right derived from the actual camera facing,
  // so "W" always means "up the screen" and "D" means screen-right, no matter
  // how the view is rotated. Right comes straight from the camera's world
  // matrix (column 0) — the canonical screen-right axis.
  _updateBasis() {
    this.camera.updateMatrixWorld();
    this.camera.getWorldDirection(this._forward);
    this._forward.y = 0;
    this._forward.normalize();
    this._right.setFromMatrixColumn(this.camera.matrixWorld, 0);
    this._right.y = 0;
    this._right.normalize();
  }

  get forward() { return this._forward; }
  get right() { return this._right; }
}
